import { useEffect, useState } from "react";

/**
 * Holds the screen awake while something is happening, and says whether it managed to.
 *
 * ## Why a platform is not enough on its own
 *
 * A phone locks its screen on a timeout that has nothing to do with what the page is doing, and
 * both ends of a live show suffer for it. For the **host** it is fatal: a locked screen stops the
 * publish, so a show lasts as long as the Android display timeout. For a **viewer** it is merely
 * ruinous — the picture goes black during a lot they were bidding on, and getting back means
 * unlocking the phone and finding the tab.
 *
 * Playing video is *often* enough to prevent sleep by itself, and that is exactly the problem: it
 * is a per-platform heuristic rather than a promise, and the room's video is a **WebRTC
 * MediaStream** rather than a file, which is the case those heuristics are least reliable about.
 *
 * ## The two details that make this work
 *
 * Both were learned in the host panel (M0.9) and are the reason this is shared rather than written
 * twice:
 *
 * - **The browser releases the lock whenever the page is hidden, and does not restore it.** So it
 *   has to be re-acquired on every `visibilitychange` — the listener is the mechanism, not
 *   housekeeping. A version without it holds the screen until the first notification and then
 *   quietly stops.
 * - **The request can simply be refused** — an unsupported browser, battery-saver mode, a page
 *   that lost visibility between the check and the call. Nothing here retries a refusal, because
 *   the next `visibilitychange` is a better trigger than a timer, and a refused lock is a
 *   preference rather than a fault.
 *
 * Supported in Safari since 16.4, so the primary platform (§18.1) is covered; everywhere else it
 * is a no-op and the answer is `false`.
 *
 * @param active Whether the screen should be held now — `state === "publishing"` for a host,
 *   `state === "live"` for a viewer. Releasing on `false` matters: a lock held after the show has
 *   ended is somebody's battery.
 * @returns Whether a lock is actually held. The host warns on `false` because the timeout is then
 *   theirs to change; a viewer has nothing to do about it and ignores this.
 */
export function useWakeLock(active: boolean): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (!active) return;

    const api = navigator as Navigator & {
      wakeLock?: {
        request: (
          type: "screen",
        ) => Promise<{ release: () => Promise<void>; released: boolean }>;
      };
    };
    if (!api.wakeLock) return;

    let alive = true;
    let sentinel: { release: () => Promise<void>; released: boolean } | undefined;

    async function acquire() {
      if (!alive || document.hidden || sentinel?.released === false) return;
      try {
        sentinel = await api.wakeLock!.request("screen");
        if (alive) setHeld(true);
        else void sentinel.release().catch(() => undefined);
      } catch {
        // Refused — battery saver, or the page lost visibility between the check and the call.
        if (alive) setHeld(false);
      }
    }

    const onVisibility = () => {
      if (document.hidden) {
        // The browser has already released it; this only keeps our own answer honest.
        setHeld(false);
        return;
      }
      void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel?.release().catch(() => undefined);
      sentinel = undefined;
      setHeld(false);
    };
  }, [active]);

  return held;
}
